/**
 * API Routes Configuration
 * Centralized API route definitions for the frontend
 *
 * All API endpoints should be defined here.
 * Use buildUrl() helper for adding query parameters.
 */

import { API_BASE_URL } from './api';

// API Base Path (all routes now under /api/*)
const API_BASE = API_BASE_URL;

/**
 * API Routes organized by module
 */
export const API_ROUTES = {
    // Admin Routes
    admin: {
        login: `${API_BASE}/auth/login`,
        register: `${API_BASE}/auth/register`,
        contacts: {
            base: `${API_BASE}/contacts`,
            getAll: `${API_BASE}/contacts`,
            getById: (id: string | number) => `${API_BASE}/contacts/${id}`,
            create: `${API_BASE}/contacts`,
            update: (id: string | number) => `${API_BASE}/contacts/${id}`,
            delete: (id: string | number) => `${API_BASE}/contacts/${id}`,
            ban: (id: string | number) => `${API_BASE}/contacts/${id}/ban`,
            unban: (id: string | number) => `${API_BASE}/contacts/${id}/unban`,
            restore: (id: string | number) => `${API_BASE}/contacts/${id}/restore`,
            relationships: (id: string | number) => `${API_BASE}/contacts/${id}/relationships`,
            relationshipsBatch: (id: string | number) => `${API_BASE}/contacts/${id}/relationships/batch`,
            relationship: (contactId: string | number, relationshipId: string | number) =>
                `${API_BASE}/contacts/${contactId}/relationships/${relationshipId}`,
            leadAssessment: (id: string | number) => `${API_BASE}/contacts/${id}/lead-assessment`,
            emailBusinessCard: (id: string | number) => `${API_BASE}/contacts/${id}/email-business-card`,
        },
        companies: {
            logo: (id: string | number) => `${API_BASE}/companies/${id}/logo`,
            fetchLogo: (id: string | number) => `${API_BASE}/companies/${id}/logo/fetch`,
        },
        properties: {
            base: `${API_BASE}/property-scraping/properties`,
            getAll: `${API_BASE}/property-scraping/properties`,
            create: `${API_BASE}/property-scraping/properties`,
        },
        agreements: {
            getById: (id: string | number) => `${API_BASE}/agreements/${id}`,
            updateStatus: (id: string | number) => `${API_BASE}/agreements/${id}/status`,
        },
        settings: {
            agentConfig: `${API_BASE}/agent-config`,
            emailBlacklist: `${API_BASE}/email-blacklist`,
        },
        auditLogs: {
            base: `${API_BASE}/audit-logs`,
            stats: `${API_BASE}/audit-logs/stats`,
        },
        users: {
            base: `${API_BASE}/users/registered`,
            getAll: `${API_BASE}/users/registered`,
            getById: (id: string | number) => `${API_BASE}/users/${id}`,
            updateStatus: (id: string | number) => `${API_BASE}/users/${id}/status`,
        },
        contactExchanges: {
            base: `${API_BASE}/contact-exchanges`,
            getAll: `${API_BASE}/contact-exchanges`,
            create: `${API_BASE}/contact-exchanges`,
            getById: (id: string | number) => `${API_BASE}/contact-exchanges/${id}`,
            updateStatus: (id: string | number) => `${API_BASE}/contact-exchanges/${id}/status`,
        },
        relationshipTypes: `${API_BASE}/contacts/relationship-types`,
    },

    // Auth Routes (now under /api/auth/)
    auth: {
        login: `${API_BASE}/auth/login`,
        phoneLogin: `${API_BASE}/auth/phone-login`,
        loginRequestCode: `${API_BASE}/auth/login/request-code`,
        loginVerifyCode: `${API_BASE}/auth/login/verify-code`,
        devLogin: `${API_BASE}/auth/dev-login`,
        register: `${API_BASE}/auth/register`,
        registerBuyer: `${API_BASE}/auth/register/buyer`,
        registerSeller: `${API_BASE}/auth/register/seller`,
        registerRealtor: `${API_BASE}/auth/register/realtor`,
        registerProfessional: `${API_BASE}/auth/register/professional`,
        me: `${API_BASE}/auth/me`,
        passkeys: {
            status: `${API_BASE}/auth/passkeys/status`,
            list: `${API_BASE}/auth/passkeys`,
            context: `${API_BASE}/auth/passkeys/context`,
            detail: (id: number) => `${API_BASE}/auth/passkeys/${id}`,
            registerOptions: `${API_BASE}/auth/passkeys/register/options`,
            registerVerify: `${API_BASE}/auth/passkeys/register/verify`,
            loginOptions: `${API_BASE}/auth/passkeys/login/options`,
            loginVerify: `${API_BASE}/auth/passkeys/login/verify`,
        },
        verifyEmail: `${API_BASE}/auth/verify-email`,
        resendVerification: `${API_BASE}/auth/resend-verification-email`,
        checkEmail: `${API_BASE}/auth/check-email`,
        verifyLicense: `${API_BASE}/auth/verify-license`,
    },

    // Public Routes
    public: {
        properties: {
            base: `${API_BASE}/property-scraping/properties`,
            list: `${API_BASE}/property-scraping/properties`,
            getById: (id: string | number) => `${API_BASE}/property-scraping/property-details/${id}`,
            forSale: `${API_BASE}/property-scraping/properties?status=for_sale`,
        },
        contact: {
            sendBusinessCard: `${API_BASE}/contact/send-business-card`,
        },
        agentProfile: `${API_BASE}/agent-config`,
    },

    // Open House Routes — public and admin share the /open-houses prefix
    // (auth is per endpoint on the backend), so one block keeps the map
    // readable. Dynamic segments follow the `(id) => template` pattern
    // used by admin.contacts above.
    openHouse: {
        base: `${API_BASE}/open-houses`,
        list: `${API_BASE}/open-houses`,
        create: `${API_BASE}/open-houses`,
        upcoming: `${API_BASE}/open-houses/upcoming`,
        detail: (id: string | number) => `${API_BASE}/open-houses/${id}`,
        close: (id: string | number) => `${API_BASE}/open-houses/${id}/close`,
        sendStats: (id: string | number) => `${API_BASE}/open-houses/${id}/send-stats`,
        qr: (id: string | number) => `${API_BASE}/open-houses/${id}/qr`,
        documents: (id: string | number) => `${API_BASE}/open-houses/${id}/documents`,
        document: (id: string | number, index: number) => `${API_BASE}/open-houses/${id}/documents/${index}`,
        publicBootstrap: (code: string) => `${API_BASE}/open-houses/public/${code}`,
        publicCis: (code: string) => `${API_BASE}/open-houses/public/${code}/cis`,
        publicSignIn: (code: string) => `${API_BASE}/open-houses/public/${code}/sign-in`,
        // Operator settings + follow-up queue + per-sign-in actions
        settings: `${API_BASE}/open-houses/settings`,
        settingsPreview: `${API_BASE}/open-houses/settings/preview`,
        followups: `${API_BASE}/open-houses/followups`,
        adminSignIns: (id: string | number) => `${API_BASE}/open-houses/${id}/sign-ins`,
        signInCancelFollowup: (sid: string | number) => `${API_BASE}/open-houses/sign-ins/${sid}/cancel-followup`,
        signInSendFollowupNow: (sid: string | number) => `${API_BASE}/open-houses/sign-ins/${sid}/send-followup-now`,
        signInResendPacket: (sid: string | number) => `${API_BASE}/open-houses/sign-ins/${sid}/resend-packet`,
        signInPdf: (sid: string | number, kind: 'signin' | 'cis') => `${API_BASE}/open-houses/sign-ins/${sid}/pdf?kind=${kind}`,
        signInErase: (sid: string | number) => `${API_BASE}/open-houses/sign-ins/${sid}/erase`,
        signInMinimize: (sid: string | number) => `${API_BASE}/open-houses/sign-ins/${sid}/minimize`,
        signInMarkUnrepresented: (sid: string | number) => `${API_BASE}/open-houses/sign-ins/${sid}/mark-unrepresented`,
        signInMarkRepresented: (sid: string | number) => `${API_BASE}/open-houses/sign-ins/${sid}/mark-represented`,
        signInMarkVisitingAgent: (sid: string | number) => `${API_BASE}/open-houses/sign-ins/${sid}/mark-visiting-agent`,
        signInBusinessCard: (sid: string | number) => `${API_BASE}/open-houses/sign-ins/${sid}/business-card`,
        publicBusinessCard: (code: string, token: string) => `${API_BASE}/open-houses/public/${code}/sign-ins/${token}/business-card`,
        publicBusinessCardStatus: (code: string, token: string) => `${API_BASE}/open-houses/public/${code}/sign-ins/${token}/business-card/status`,
        retention: `${API_BASE}/open-houses/retention`,
    },
} as const;

/**
 * Build full URL with query parameters
 * Handles URLs that already contain query parameters
 */
export const buildUrl = (
    baseUrl: string,
    params?: Record<string, string | number | boolean | undefined | null>
): string => {
    if (!params) return baseUrl;

    // Parse existing URL to handle URLs with existing query params
    const [path = baseUrl, existingQuery] = baseUrl.split('?');

    const queryParams = new URLSearchParams(existingQuery || '');

    Object.entries(params).forEach(([key, value]) => {
        if (value !== undefined && value !== null) {
            queryParams.set(key, String(value));
        }
    });

    const queryString = queryParams.toString();
    return queryString ? `${path}?${queryString}` : path;
};

export default API_ROUTES;
